import localforage from 'localforage';

import type {
  GetDisplayCategoriesQuery,
  GetDisplayCategoriesQueryVariables,
  OffersQuery,
  OffersQueryVariables,
} from '@ocx/graphql';
import { logInDev } from '@ocx/utils';

import {
  DISPLAY_CATEGORIES_CACHE_TTL_MS,
  DISPLAY_CATEGORIES_QUERY_KEY,
  OFFERS_CACHE_MAX_GEO_ENTRIES,
  OFFERS_CACHE_TTL_MS,
  OFFERS_QUERY_KEY_PREFIX,
} from './offers-cache.constants';
import { bucketGeoInQueryVariables, stableStringify } from './offers-cache.utils';

// TODO: extract `cacheStorage` to a shared lib so this isn't duplicated with
// `apps/frontend/src/modules/cache-layer/cacheStorage.ts`. They must currently
// stay in sync (same name + storeName) so `clearCache()` in the app layer also
// wipes entries written here. If either side changes the config, the other
// breaks silently.
const storage = localforage.createInstance({
  name: 'Experience',
  storeName: 'ocx-cache',
  description: 'Stores data to speed up app loading',
});

export type OffersQueryCacheEntry = {
  data: OffersQuery;
  /** Precise variables used for the original query — written back to Apollo on restore. */
  variables: OffersQueryVariables;
  savedAt: string;
};

const isGeoEntry = (entry: OffersQueryCacheEntry): boolean => !!entry.variables.locationsGeoFilter;

export type DisplayCategoriesCacheEntry = {
  data: GetDisplayCategoriesQuery;
  variables: GetDisplayCategoriesQueryVariables;
  savedAt: string;
};

const isExpired = (savedAt: string, ttlMs: number): boolean => Date.now() - new Date(savedAt).getTime() > ttlMs;

/** Cache key — geo bucketed so sessions at the same location share one entry. */
const offersQueryKey = (vars: OffersQueryVariables): string =>
  OFFERS_QUERY_KEY_PREFIX + stableStringify(bucketGeoInQueryVariables(vars));

const listOffersEntries = async (): Promise<Array<{ key: string; entry: OffersQueryCacheEntry }>> => {
  const keys = (await storage.keys()).filter((k: string) => k.startsWith(OFFERS_QUERY_KEY_PREFIX));
  const pairs = await Promise.all(
    keys.map(async (key: string) => ({ key, entry: await storage.getItem<OffersQueryCacheEntry>(key) })),
  );
  return pairs.filter((p): p is { key: string; entry: OffersQueryCacheEntry } => !!p.entry);
};

export const getCachedOffersQueries = async (): Promise<OffersQueryCacheEntry[]> => {
  try {
    const pairs = await listOffersEntries();
    return pairs.map(({ entry }) => entry).filter((e) => !isExpired(e.savedAt, OFFERS_CACHE_TTL_MS));
  } catch (e) {
    logInDev(e);
    return [];
  }
};

const pruneOldestGeoEntries = async (): Promise<void> => {
  try {
    const pairs = await listOffersEntries();
    const geoEntries = pairs
      .filter(({ entry }) => isGeoEntry(entry))
      .sort((a, b) => new Date(b.entry.savedAt).getTime() - new Date(a.entry.savedAt).getTime());
    await Promise.all(geoEntries.slice(OFFERS_CACHE_MAX_GEO_ENTRIES).map(({ key }) => storage.removeItem(key)));
  } catch (e) {
    logInDev(e);
  }
};

export const setCachedOffersQuery = async (entry: OffersQueryCacheEntry): Promise<void> => {
  try {
    await storage.setItem(offersQueryKey(entry.variables), entry);
    if (isGeoEntry(entry)) {
      await pruneOldestGeoEntries();
    }
  } catch (e) {
    logInDev(e);
  }
};

export const getCachedDisplayCategories = async (): Promise<DisplayCategoriesCacheEntry | null> => {
  try {
    const entry = await storage.getItem<DisplayCategoriesCacheEntry>(DISPLAY_CATEGORIES_QUERY_KEY);
    if (!entry) return null;
    if (isExpired(entry.savedAt, DISPLAY_CATEGORIES_CACHE_TTL_MS)) return null;
    return entry;
  } catch (e) {
    logInDev(e);
    return null;
  }
};

export const setCachedDisplayCategories = async (entry: DisplayCategoriesCacheEntry): Promise<void> => {
  try {
    await storage.setItem(DISPLAY_CATEGORIES_QUERY_KEY, entry);
  } catch (e) {
    logInDev(e);
  }
};
